import React, { useEffect, useRef, useState } from "react";

export default function Dropdown({
  values,
  label,
  handleUpdate,
  placeholder = "Please Select…",
}) {
  const [isOpen, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const dropdownRef = useRef(null);

  // Close when user clicks outside or presses Escape
  useEffect(() => {
    const closeOnOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const closeOnEsc = (e) => e.key === "Escape" && setOpen(false);

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEsc);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEsc);
    };
  }, []);

  const toggleOpen = () => setOpen((prev) => !prev);
  const selectValue = (value) => {
    handleUpdate(value);
    setSelectedValue(value);
    setOpen(false);
  };

  const styles = {
    wrapper: {
      position: "relative",
      minWidth: "11.25rem",
      fontFamily: "inherit",
    },
    label: {
      position: "absolute",
      top: "-0.875rem",
      left: "0.75rem",
      fontSize: "0.75rem",
      fontWeight: 600,
      color: "#333",
    },
    button: {
      width: "100%",
      background: "#fff",
      border: "1px solid #ccc",
      borderRadius: "0.5rem",
      padding: "0.5rem 0.75rem",
      fontSize: "0.875rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      transition: "background .2s",
    },
    buttonHover: {
      background: "#f1f1f1",
    },
    listbox: {
      position: "absolute",
      top: "calc(100% + 0.375rem)",
      left: 0,
      width: "100%",
      background: "#fff",
      border: "1px solid #ccc",
      borderRadius: "0.5rem",
      margin: 0,
      padding: "0.25rem 0",
      listStyle: "none",
      boxShadow: "0 0.5rem 1rem rgba(0,0,0,.05)",
      zIndex: 1000,
    },
    listItem: {
      padding: "0.4375rem 0.75rem",
      cursor: "pointer",
      borderRadius: "0.375rem",
      transition: "background .2s",
    },
    listItemHover: {
      background: "#f1f1f1",
    },
    mutedText: {
      color: "#888",
    },
    caret: {
      fontSize: "0.75rem",
      marginLeft: "0.5rem",
      transition: "transform .2s",
      userSelect: "none",
    },
  };

  const [buttonHover, setButtonHover] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <div ref={dropdownRef} style={styles.wrapper}>
      <span style={styles.label}>{label}</span>

      <div
        role="button"
        tabIndex={0}
        style={{
          ...styles.button,
          ...(buttonHover ? styles.buttonHover : {}),
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={toggleOpen}
        onKeyDown={(e) => e.key === "Enter" && toggleOpen()}
        onMouseEnter={() => setButtonHover(true)}
        onMouseLeave={() => setButtonHover(false)}
      >
        <span style={selectedValue ? {} : styles.mutedText}>
          {selectedValue || placeholder}
        </span>
        <span
          style={{
            ...styles.caret,
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          ▼
        </span>
      </div>

      {isOpen && (
        <ul style={styles.listbox} role="listbox">
          {values.map((value, idx) => (
            <li
              key={idx}
              role="option"
              aria-selected={selectedValue === value}
              style={{
                ...styles.listItem,
                ...(hoveredIndex === idx ? styles.listItemHover : {}),
              }}
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => selectValue(value)}
            >
              {value}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

